// disabling eslint temporarily for legacy files until they can be refactored
/* eslint-disable */
import React, { useEffect, useState } from "react";
import clsx from "clsx";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import {
  Portlet,
  PortletBody,
  PortletHeader,
} from "../../../components/GenericPortlet/Portlet";
import { Spinner } from "../../../components/Spinner";
import { Col, Row } from "react-bootstrap";
import { GenericProgressBar } from "../../../components/GenericProgressBar";
import { GenericLegend } from "../Legends/GenericLegend";
import language from "../../utils/en.json";
import countryMappings from "../../utils/CountryCoordinatesMapping";
import styles from "./worldMap.module.scss";

export const WorldMap = (props) => {
  const {
    isLoading,
    portletTitle,
    markers,
    legendData,
    maxLegendLength,
    mapProjectionConfig,
    showProgressBarsWithLegend = true,
    mapColSize = 8,
    showHeader = true,
    countriesColorData,
  } = props;

  const [isError, setIsError] = useState(false); // eslint-disable-line no-unused-vars
  const [translatedLegendData, setTranslatedLegendData] = useState();

  const mapUrl =
    "https://augmentt-public-files.s3.amazonaws.com/world_map_without_antarctica.json";

  // Calculated the total risk for a given country
  const calculateTotalRisk = (countryObject) => {
    let totalRisk = 0;

    totalRisk =
      (countryObject?.highRisks || 0) +
      (countryObject?.mediumRisks || 0) +
      (countryObject?.lowRisks || 0) +
      (countryObject?.informationalRisks || 0) +
      (countryObject?.unknownRisks || 0);

    return totalRisk;
  };

  const getCountryColor = (geo) => {
    return !!countriesColorData && countriesColorData[geo?.properties?.name]
      ? countriesColorData[geo?.properties?.name]
      : "#EFF0F6";
  };

  // Handles calculating percentage scaling of the legend width
  useEffect(() => {
    setTranslatedLegendData(); // Reset legend data

    if (!markers?.length || !legendData?.length) {
      setIsError(true);
      return;
    } else {
      setIsError(false);

      let totalRiskOfRiskiestCountry = 0;
      legendData.forEach((item) => {
        const sum =
          (item.highRisks || 0) +
          (item.mediumRisks || 0) +
          (item.lowRisks || 0) +
          (item.informationalRisks || 0) +
          (item.unknownRisks || 0);
        if (totalRiskOfRiskiestCountry < sum) {
          totalRiskOfRiskiestCountry = sum;
        }
      });

      // Calculate the percentage of each progress bar compared to the country of highest risk. (Everything will scale down based on the country with highest risk to ensure legend always fits total container width)
      let scaledLegendData = legendData;
      scaledLegendData = scaledLegendData.map((countryObject) => {
        const countryTotal =
          (countryObject.highRisks || 0) +
          (countryObject.mediumRisks || 0) +
          (countryObject.lowRisks || 0) +
          (countryObject.informationalRisks || 0) +
          (countryObject.unknownRisks || 0);
        const width = (countryTotal / totalRiskOfRiskiestCountry) * 100;

        return {
          width: width,
          country:
            countryMappings?.[countryObject?.country]?.name ??
            countryObject?.country,
          highRisksPercentage:
            ((countryObject?.highRisks ? countryObject.highRisks : 0) /
              countryTotal) *
            100,
          mediumRisksPercentage:
            ((countryObject?.mediumRisks ? countryObject.mediumRisks : 0) /
              countryTotal) *
            100,
          lowRisksPercentage:
            ((countryObject?.lowRisks ? countryObject.lowRisks : 0) /
              countryTotal) *
            100,
          informationalRisksPercentage:
            ((countryObject?.informationalRisks || 0) / countryTotal) * 100,
          unknwonRisksPercentage:
            ((countryObject?.unknwonRisks || 0) / countryTotal) * 100,
          totalRiskCount: calculateTotalRisk(countryObject),
        };
      });

      setTranslatedLegendData(scaledLegendData);
    }
  }, [markers, legendData]);

  return (
    <>
      <Portlet className="portlet--border-bottom-dark">
        {/* Loading view */}
        {isLoading ? (
          <>
            <Spinner />
          </>
        ) : (
          <>
            {/* Main view */}
            {showHeader && (
              <PortletHeader
                title={
                  portletTitle ? portletTitle : language["SECURE.WORLD_MAP"]
                }
              />
            )}
            <hr className="m0"></hr>
            <PortletBody>
              <Row className={clsx(styles.worldMapPortlet, "pt-2 pb-2")}>
                <Col sm={mapColSize} md={mapColSize} lg={mapColSize}>
                  <div className={styles["map-container"]}>
                    <ComposableMap
                      projectionConfig={
                        mapProjectionConfig
                          ? mapProjectionConfig
                          : {
                              scale: 145,
                              rotation: [-11, 0, 0],
                            }
                      }
                      width={800}
                      height={400}
                      style={{ width: "100%", height: "auto" }}
                    >
                      <Geographies geography={mapUrl}>
                        {({ geographies }) =>
                          geographies?.map((geo) => (
                            <Geography
                              key={geo.rsmKey}
                              geography={geo}
                              style={{
                                default: {
                                  fill: getCountryColor(geo),
                                  stroke: "#FFFFFF",
                                  strokeWidth: "1px",
                                },
                                hover: {
                                  fill: "#F53",
                                },
                                pressed: {
                                  fill: "#E42",
                                },
                              }}
                            />
                          ))
                        }
                      </Geographies>
                      {markers
                        .filter(({ coordinates }) => coordinates[0] !== null)
                        .map(({ risk, coordinates, radius }, index) => (
                          <Marker key={index} coordinates={coordinates}>
                            <circle
                              r={radius}
                              fill={
                                risk === "high"
                                  ? "#FF7470"
                                  : risk === "medium"
                                  ? "#FFACA9"
                                  : risk === "low"
                                  ? "#FFD5D4"
                                  : risk === "informational"
                                  ? "#e1e1ef"
                                  : "#ccc"
                              }
                              stroke="#fff"
                              strokeWidth={1}
                            />
                          </Marker>
                        ))}
                    </ComposableMap>
                  </div>
                </Col>

                {showProgressBarsWithLegend ? (
                  <Col sm="4" md="4" lg="4">
                    <div className={styles["map-legend"]}>
                      {translatedLegendData &&
                        translatedLegendData.length &&
                        translatedLegendData.map((countryObject, index) => {
                          if (index >= maxLegendLength) {
                            return null;
                          } else {
                            return (
                              <div
                                key={index}
                                className={"p-2" + (index >= 1 ? " mt-2" : "")}
                              >
                                <Row>
                                  <Col>
                                    <GenericProgressBar
                                      style={{
                                        width: countryObject.width + "%",
                                      }}
                                      isMultiValueProgressBar={true}
                                      values={[
                                        countryObject.highRisksPercentage,
                                        countryObject.mediumRisksPercentage,
                                        countryObject.lowRisksPercentage,
                                        countryObject.informationalRisksPercentage,
                                        countryObject.unknownRisksPercentage,
                                      ]}
                                      showLegendFooter={false}
                                      showPercentage={false}
                                      showScore={false}
                                      showTopLabel={true}
                                      showRightLabel={true}
                                      rightLabelString={
                                        countryObject.totalRiskCount
                                      }
                                      topLabelString={
                                        countryObject?.country
                                          ? countryObject.country
                                          : ""
                                      }
                                      showFractionalFooter={false}
                                      showThreeValueFooter={false}
                                      colors={[
                                        "#FF7470",
                                        "#FFACA9",
                                        "#FFD5D4",
                                        "#e1e1ef",
                                        "#ccc",
                                      ]}
                                      maxLabelDepth={1}
                                    />
                                  </Col>
                                </Row>
                              </div>
                            );
                          }
                        })}

                      <Row>
                        <Col>
                          <GenericLegend
                            colors={[
                              "#FF7470",
                              "#FFACA9",
                              "#FFD5D4",
                              "#e1e1ef",
                              "#ccc",
                            ]}
                            labels={[
                              language["SECURE.HIGH_RISK"],
                              language["SECURE.MED_RISK"],
                              language["SECURE.LOW_RISK"],
                              language["SECURE.INFORMATIONAL_RISK"],
                              language["SECURE.UNKNOWN_RISK"],
                            ]}
                          />
                        </Col>
                      </Row>
                    </div>
                  </Col>
                ) : (
                  <GenericLegend
                    colors={[
                      "#FF7470",
                      "#FFACA9",
                      "#FFD5D4",
                      "#e1e1ef",
                      "#ccc",
                    ]}
                    labels={[
                      language["SECURE.POLICY_BLOCKING_ACCESS"],
                      language["SECURE.POLICY_CHALLENGING_ACCESS"],
                      language["SECURE.NO_POLICY"],
                    ]}
                  />
                )}
              </Row>
            </PortletBody>
          </>
        )}
      </Portlet>
    </>
  );
};
